import * as React from "react";
import { type CSSProperties, type ReactNode, useContext } from "react";
import classNames from "classnames";
import { createSelector } from "reselect";
import { Collapse, styled } from "~/common/mui";
import { getFontStyle, FontSize } from "../materialInput/Fonts";
import { C_ON_SURFACE, C_OUTLINE_VARIANT, C_SURFACE } from "../icon/Colors";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

type Themes = "stripes" | "separatorsTop" | "separators" | "none";

interface ContextProps {
  noPadding: boolean;
  columnFlex: string[][] | string[];
  theme: Themes;
}

const TableContext = React.createContext<ContextProps>({
  noPadding: false,
  columnFlex: [],
  theme: "stripes",
});
const HeaderContext = React.createContext<boolean>(false);

interface DataTableProps {
  header?: ReactNode;
  className?: string;
  noPadding?: boolean;
  columnFlex?: string[][] | string[];
  theme?: Themes;
  children?: ReactNode;
}
const EMPTY_LIST: string[] = [];
const providerDataSelector = createSelector(
  (props: DataTableProps) => !!props.noPadding,
  (props: DataTableProps) => props.columnFlex || EMPTY_LIST,
  (props: DataTableProps) => props.theme || "stripes",
  (noPadding, columnFlex, theme) => ({ noPadding, columnFlex, theme }),
);

const DataTableRoot = styled("div")({
  color: C_ON_SURFACE,
  display: "flex",
  flexDirection: "column",
});

const DataTableHeader = styled("div")({
  ...getFontStyle(FontSize.BodyMedium),
  fontWeight: "bold",
  borderBottom: `3px solid ${C_OUTLINE_VARIANT}`,
  display: "flex",
});

const DataTableBody = styled("div")({});

const StripedItem = styled("div", {
  shouldForwardProp: (prop) => typeof prop === "string" && !prop.startsWith("$"),
})<{ $isOdd?: boolean }>(({ $isOdd }) => {
  if ($isOdd === true) {
    return { backgroundColor: "#fff" };
  }
  if ($isOdd === false) {
    return { backgroundColor: C_SURFACE };
  }
  return {
    "&:nth-child(odd)": { backgroundColor: "#fff" },
    "&:nth-child(even)": { backgroundColor: C_SURFACE },
  };
});

const SeparatedItem = styled("div")({
  "& + &": {
    borderTop: `1px solid ${C_OUTLINE_VARIANT}`,
  },
});

const ExpandableItemWrapper = styled("div", {
  shouldForwardProp: (prop) => prop !== "$isExpanded",
})<{ $isExpanded?: boolean }>(({ $isExpanded }) => ({
  cursor: "pointer",
  transition: "margin-bottom 0.5s ease",
  marginBottom: $isExpanded ? "20px" : 0,
  display: "flex",
  flexDirection: "column",
}));

const ItemCard = styled("div", {
  shouldForwardProp: (prop) => prop !== "$isExpanded",
})<{ $isExpanded?: boolean }>(({ $isExpanded }) => ({
  padding: "0 10px",
  transition: "border-color 0.5s ease",
  border: "1px solid transparent",
  ...($isExpanded && {
    borderRadius: "8px",
    border: `1px solid ${C_OUTLINE_VARIANT}`,
  }),
}));

const StripedCellRow = styled("div")({
  display: "flex",
  width: "100%",
  "&:nth-child(odd)": { backgroundColor: "#fff" },
  "&:nth-child(even)": { backgroundColor: C_SURFACE },
});

const SeparatedCellRow = styled("div")({
  display: "flex",
  width: "100%",
  "& + &": {
    borderBottom: `1px solid ${C_OUTLINE_VARIANT}`,
  },
});

const SeparatedTopCellRow = styled("div")({
  display: "flex",
  width: "100%",
  "& + &": {
    borderTop: `1px solid ${C_OUTLINE_VARIANT}`,
  },
});

const PlainCellRow = styled("div")({
  display: "flex",
  width: "100%",
});

const StyledCell = styled("div", {
  shouldForwardProp: (prop) => prop !== "$noPadding",
})<{ $noPadding?: boolean }>(({ $noPadding }) => ({
  ...getFontStyle(FontSize.BodyMedium),
  flex: 1,
  padding: $noPadding ? 0 : "10px 0",
  "& .scha__link": {
    fontWeight: "normal",
  },
}));

/**
 *
 * @deprecated
 */
export const Table = (props: DataTableProps) => {
  const { className, children, header } = props;
  return (
    <TableContext.Provider value={providerDataSelector(props)}>
      <DataTableRoot className={classNames("scha__data-table", className)}>
        {header && (
          <DataTableHeader className="scha__data-table__header">
            <HeaderContext.Provider value={true}>{header}</HeaderContext.Provider>
          </DataTableHeader>
        )}
        <DataTableBody className="scha__data-table__body">{children}</DataTableBody>
      </DataTableRoot>
    </TableContext.Provider>
  );
};

export const TableItem: React.FC<{
  className?: string;
  style?: CSSProperties;
  isOdd?: boolean;
  children?: ReactNode;
}> = (props) => {
  const { className, isOdd, children, ...rest } = props;

  const contextProps = useContext(TableContext);
  const oddEvenCls =
    isOdd !== undefined ? (isOdd ? "scha__data-table__item--odd-row" : "scha__data-table__item--even-row") : "";

  if (contextProps.theme === "stripes") {
    return (
      <StripedItem $isOdd={isOdd} className={classNames("scha__data-table__item", "scha__data-table__item--stripes", oddEvenCls, className)} {...rest}>
        {children}
      </StripedItem>
    );
  }

  if (contextProps.theme === "separators") {
    return (
      <SeparatedItem className={classNames("scha__data-table__item", `scha__data-table__item--${contextProps.theme}`, className)} {...rest}>
        {children}
      </SeparatedItem>
    );
  }

  return (
    <div className={classNames("scha__data-table__item", `scha__data-table__item--${contextProps.theme}`, oddEvenCls, className)} {...rest}>
      {children}
    </div>
  );
};

interface ExpandableTableItemProps {
  header: ReactNode;
  isExpanded?: boolean;
  onClick: (e: React.MouseEvent<HTMLElement>) => void;
  children?: ReactNode;
}

export const ExpandableTableItem: React.FC<ExpandableTableItemProps & JSX.IntrinsicElements["div"]> = (props) => {
  const { children, className, isExpanded, header, ...divProps } = props;

  return (
    <ExpandableItemWrapper $isExpanded={isExpanded} className={classNames("scha__data-table__item", "scha__data-table__item--expandable", isExpanded && "expanded", className)} {...divProps}>
      <ItemCard $isExpanded={isExpanded} className={classNames("scha__data-table__item__card", isExpanded && "expanded")}>
        {header}
        <Collapse in={isExpanded}>{children}</Collapse>
      </ItemCard>
    </ExpandableItemWrapper>
  );
};

interface ExpandableTableItemIndicatorProps {
  className?: string;
  isExpanded?: boolean;
}

export const ExpandableTableItemIndicator: React.FC<ExpandableTableItemIndicatorProps> = (props) => {
  const { isExpanded, className } = props;
  const IndicatorIcon = isExpanded ? KeyboardArrowUpIcon : KeyboardArrowDownIcon;

  return <IndicatorIcon className={className}  />;
};

interface CellRowProps {
  className?: string;
  noPadding?: boolean;
  columnFlex?: string[][] | string[];
  children?: ReactNode;
}

export const CellRow: React.FC<CellRowProps> = (props) => {
  const { className, children } = props;

  const contextProps = useContext(TableContext);
  const columnFlex = props.columnFlex || contextProps.columnFlex;
  const noPadding = props.noPadding !== undefined ? props.noPadding : contextProps.noPadding;
  const themeClass = `scha__data-table__cell-row--${contextProps.theme}`;

  const isHeader = useContext(HeaderContext);

  const RowComponent =
    contextProps.theme === "stripes"
      ? StripedCellRow
      : contextProps.theme === "separators"
        ? SeparatedCellRow
        : contextProps.theme === "separatorsTop"
          ? SeparatedTopCellRow
          : PlainCellRow;

  return (
    <RowComponent className={classNames("scha__data-table__cell-row", themeClass, className)}>
      {React.Children.map(children, (child, index) => {
        if (child && typeof child === "object" && "type" in child && child.type === Cell) {
          return React.cloneElement(child, {
            columnFlex: columnFlex[index],
            noPadding,
            isHeader,
            ...child.props,
          });
        }
        return child;
      })}
    </RowComponent>
  );
};

interface CellProps {
  className?: string;
  noPadding?: boolean;
  columnFlex?: string[] | string;
  isHeader?: boolean;
  children?: ReactNode;
}

const convertColumnFlexToStyle = (columnFlex?: string | string[]) => {
  if (!columnFlex) {
    return undefined;
  }
  if (!Array.isArray(columnFlex)) {
    return { flex: columnFlex };
  }
  if (columnFlex.length === 3) {
    return {
      flexGrow: columnFlex[0],
      flexShrink: columnFlex[1],
      flexBasis: columnFlex[2],
    };
  }
  return { flex: columnFlex.join(" ") };
};

export const Cell: React.FC<CellProps> = (props) => {
  const { className, noPadding, columnFlex, children } = props;

  const style = convertColumnFlexToStyle(columnFlex);

  return (
    <StyledCell
      $noPadding={noPadding}
      className={classNames("scha__data-table__cell", noPadding && "scha__data-table__cell--no-padding", className)}
      style={style}
    >
      {children}
    </StyledCell>
  );
};

export const generateFlexFromPercentages = (percentages: number[]) =>
  percentages.map((value) => ["0", "0", `${value * 100}%`]);

export const generateNestedFlexFromPercentages = (
  percentages: number[],
  paddingLeft: number,
  paddingRight: number = paddingLeft,
) =>
  percentages.map((value, index) => {
    const totalPadding = paddingLeft + paddingRight;
    const isFirst = index === 0;
    if (isFirst) {
      return ["0", "0", `calc((100% + ${totalPadding}px) * ${value} - ${paddingLeft}px)`];
    }
    const isLast = index === percentages.length - 1;
    if (isLast) {
      return ["0", "0", `calc((100% + ${totalPadding}px) * ${value} - ${paddingRight}px)`];
    }
    return ["0", "0", `calc((100% + ${totalPadding}px) * ${value}`];
  });

const DataTable = {
  Table,
  TableItem,
  ExpandableTableItem,
  ExpandableTableItemIndicator,
  CellRow,
  Cell,
  generateFlexFromPercentages,
  generateNestedFlexFromPercentages,
};

export default DataTable;
